/**
 * Discovery > DPIA data elements (3 Nov 2026)
 *
 * The data elements an approved FOCTTA Consent DPIA sent to Discovery, and
 * those entered here, grouped by the DPIA and version they came from. Consent's
 * Data elements tab says "Delivered to FOCTTA Discovery"; this is where they
 * land. Each row says which classifier label it maps to (or that it became a
 * keyword rule) and whether the scan applies it now, with the reason when not.
 *
 * Read only. The list route is discovery:read, and row-level security on
 * declared_data_elements (migration 0252, the 0048 unit policy) limits a
 * unit-scoped user to their units' elements and the tenant-wide ones, so the
 * page filters nothing for access itself.
 *
 * "Received" is when the approved version reached Discovery (the row's
 * created time). Consent's own approval time travels in the event but is kept
 * only in the audit record, not on the row.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { AlertTriangle, CheckCircle2, ExternalLink, FileCheck2, Loader2, PauseCircle, RefreshCw, XCircle } from 'lucide-react';

import { GlassCard, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { PageGuide } from '@/components/ui/page-guide';
import { PageContainer, PageHeader } from '@/components/viz/PageHeader';
import { getAuthUser, listBusinessUnits } from '@/lib/api';
import { consentLinkHref } from '@/components/shell/TopBar';
import { canOpen, useCaller } from '@/lib/access';
import {
  listDeclaredElements,
  type DeclaredElement,
  type DeclaredStatus,
  type ScanReason,
} from '@/lib/declared-elements-api';

export const HELD_UNIT_TEXT = 'Held: unit not synced yet';
const ALL_UNITS_TEXT = 'All units';
const MANUAL_GROUP = 'manual';

/** Business-unit filter values that are not a unit id. */
export const UNIT_FILTER_TENANT_WIDE = '__tenant_wide';
export const UNIT_FILTER_HELD = '__held';

const REASON_TEXT: Record<ScanReason, string> = {
  retired: 'Retired',
  unmapped: 'Nothing to scan for: no label or keyword',
  unit_not_synced: 'Held until its business unit syncs from FOCTTA Consent',
  over_cap: 'Some keywords left out: the declared-rule limit is reached',
  rule_disabled: 'Keyword rule switched off',
  label_switched_off: 'Label switched off in detector settings',
};

const RETIRED_TEXT: Record<string, string> = {
  superseded: 'Left out of a later approved version',
  withdrawn: 'DPIA withdrawn in FOCTTA Consent',
  removed: 'Removed in Discovery',
};

function ist(iso: string | null): string {
  if (!iso) return '-';
  return `${new Date(iso).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })} IST`;
}

export function isHeld(e: DeclaredElement): boolean {
  return e.businessUnitId === null && e.coreBusinessUnitRef !== null;
}

export function unitText(e: DeclaredElement, unitNames: ReadonlyMap<string, string>): string {
  if (e.businessUnitId) return unitNames.get(e.businessUnitId) ?? `Unit ${e.businessUnitId.slice(0, 8)}`;
  return isHeld(e) ? HELD_UNIT_TEXT : ALL_UNITS_TEXT;
}

export function matchesUnit(e: DeclaredElement, unit: string): boolean {
  if (unit === 'all') return true;
  if (unit === UNIT_FILTER_HELD) return isHeld(e);
  if (unit === UNIT_FILTER_TENANT_WIDE) return e.businessUnitId === null && !isHeld(e);
  return e.businessUnitId === unit;
}

export function groupKey(e: DeclaredElement): string {
  return e.source === 'core_dpia' ? `dpia:${e.sourceRef ?? e.sourceReference ?? 'unknown'}` : MANUAL_GROUP;
}

export interface ElementGroup {
  key: string;
  source: DeclaredElement['source'];
  sourceRef: string | null;
  /** DPIA-2026-017, or null for elements entered in Discovery. */
  reference: string | null;
  version: number | null;
  elements: DeclaredElement[];
}

/** One group per DPIA (and one for elements entered in Discovery), DPIAs by reference, manual last. */
export function groupElements(rows: ReadonlyArray<DeclaredElement>): ElementGroup[] {
  const byKey = new Map<string, ElementGroup>();
  for (const e of rows) {
    const key = groupKey(e);
    let g = byKey.get(key);
    if (!g) {
      g = { key, source: e.source, sourceRef: e.sourceRef, reference: e.sourceReference, version: e.sourceVersion, elements: [] };
      byKey.set(key, g);
    }
    if (e.sourceVersion !== null && (g.version === null || e.sourceVersion > g.version)) g.version = e.sourceVersion;
    g.elements.push(e);
  }
  for (const g of byKey.values()) g.elements.sort((a, b) => a.name.localeCompare(b.name));
  return [...byKey.values()].sort((a, b) => {
    if (a.key === MANUAL_GROUP) return 1;
    if (b.key === MANUAL_GROUP) return -1;
    return (a.reference ?? '').localeCompare(b.reference ?? '');
  });
}

/** The DPIA in FOCTTA Consent (its assessment page), when the session knows Consent's address. */
export function consentDpiaHref(consentUrl: string | undefined, allowed: boolean, sourceRef: string | null): string | null {
  const base = consentLinkHref(consentUrl, allowed);
  if (!base || !sourceRef) return null;
  const u = new URL(base);
  u.pathname = `${u.pathname.replace(/\/+$/, '')}/assessments/${encodeURIComponent(sourceRef)}`;
  u.search = '';
  u.hash = '';
  return u.toString();
}

function mapsTo(e: DeclaredElement): { text: string; detail: string | null } {
  if (e.mappingStatus === 'mapped') return { text: e.resolvedLabel ?? '-', detail: null };
  if (e.mappingStatus === 'keyword_rule') {
    return { text: 'Keyword rule', detail: e.ruleKeywords.length > 0 ? e.ruleKeywords.join(', ') : null };
  }
  return { text: 'Not mapped', detail: null };
}

function ScanCell({ e, unitNames }: { e: DeclaredElement; unitNames: ReadonlyMap<string, string> }): JSX.Element {
  if (e.status === 'retired') {
    return (
      <span className="text-xs text-muted-foreground" data-scan="retired">
        Retired{e.retiredReason ? `: ${RETIRED_TEXT[e.retiredReason] ?? e.retiredReason}` : ''}
        {e.retiredAt ? <span className="block">{ist(e.retiredAt)}</span> : null}
      </span>
    );
  }
  const offIn = e.scan.labelSwitchedOffInUnits.map((u) => unitNames.get(u) ?? `Unit ${u.slice(0, 8)}`);
  const reasons = e.scan.reasons.map((r) =>
    r === 'over_cap' ? `${REASON_TEXT.over_cap} (${e.scan.keywordsOverCap.length} of ${e.ruleKeywords.length})` : REASON_TEXT[r] ?? r,
  );
  const held = e.scan.reasons.includes('unit_not_synced');
  return (
    <div data-scan={e.scan.inEffect ? 'in-effect' : held ? 'held' : 'not-in-effect'}>
      {e.scan.inEffect ? (
        <Badge variant="success" className="text-[10px]">
          <CheckCircle2 className="-mt-0.5 mr-1 inline h-3 w-3" />
          In effect
        </Badge>
      ) : (
        <Badge variant={held ? 'warning' : 'outline'} className="text-[10px]">
          {held ? <PauseCircle className="-mt-0.5 mr-1 inline h-3 w-3" /> : <XCircle className="-mt-0.5 mr-1 inline h-3 w-3" />}
          {held ? 'Held' : 'Not in effect'}
        </Badge>
      )}
      {reasons.length > 0 ? (
        <ul className="mt-1 space-y-0.5 text-[11px] text-muted-foreground">
          {reasons.map((r) => (
            <li key={r}>{r}</li>
          ))}
        </ul>
      ) : null}
      {offIn.length > 0 ? (
        <p className="mt-1 text-[11px] text-muted-foreground">Switched off in {offIn.join(', ')}</p>
      ) : null}
    </div>
  );
}

export function DeclaredElements(): JSX.Element {
  const access = useCaller();
  const caller = access.status === 'ready' ? access.caller : null;
  const consentUrl = getAuthUser()?.consentUrl;
  const consentAllowed = canOpen('disc.back_to_consent', caller);

  const [status, setStatus] = useState<DeclaredStatus>('active');
  const [rows, setRows] = useState<DeclaredElement[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [unitNames, setUnitNames] = useState<Map<string, string>>(new Map());
  const [dpiaFilter, setDpiaFilter] = useState('all');
  const [unitFilter, setUnitFilter] = useState('all');

  const refresh = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      setRows(await listDeclaredElements(status));
    } catch (err) {
      setRows([]);
      setLoadError(`The declared data elements could not be loaded: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Unit names come from foundational:read, which not every discovery:read
  // role holds (the Security Operator does not). Without it a row shows the
  // unit's short id rather than failing the page.
  useEffect(() => {
    let live = true;
    listBusinessUnits()
      .then((res) => {
        if (live) setUnitNames(new Map(res.businessUnits.map((u) => [u.id, u.name])));
      })
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, []);

  const allGroups = useMemo(() => groupElements(rows), [rows]);
  const unitOptions = useMemo(() => {
    const ids = [...new Set(rows.flatMap((e) => (e.businessUnitId ? [e.businessUnitId] : [])))];
    return ids
      .map((id) => ({ id, name: unitNames.get(id) ?? `Unit ${id.slice(0, 8)}` }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [rows, unitNames]);
  const hasHeld = rows.some(isHeld);
  const hasTenantWide = rows.some((e) => e.businessUnitId === null && !isHeld(e));

  const groups = useMemo(
    () =>
      allGroups
        .filter((g) => dpiaFilter === 'all' || g.key === dpiaFilter)
        .map((g) => ({ ...g, elements: g.elements.filter((e) => matchesUnit(e, unitFilter)) }))
        .filter((g) => g.elements.length > 0),
    [allGroups, dpiaFilter, unitFilter],
  );
  const shown = groups.reduce((n, g) => n + g.elements.length, 0);
  const filtered = dpiaFilter !== 'all' || unitFilter !== 'all';

  return (
    <PageContainer>
      <PageHeader
        title="DPIA data elements"
        subtitle="The personal data elements each approved DPIA in FOCTTA Consent declared, and how Discovery scans for them."
        actions={
          <Button size="sm" variant="glass" onClick={() => void refresh()} disabled={loading} aria-label="Refresh">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            Refresh
          </Button>
        }
      />

      <PageGuide id="declared-elements">
        When a DPIA is approved in FOCTTA Consent, its data elements come here. Each one maps to a label the
        classifier already detects, or becomes a keyword rule. Findings then show personal data found that no
        DPIA declared, and declared elements the scan never found.
      </PageGuide>

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <label className="glass-lite flex items-center gap-1.5 rounded-md px-2 py-1 text-xs">
          <span className="text-muted-foreground">DPIA</span>
          <select
            aria-label="DPIA"
            value={dpiaFilter}
            onChange={(e) => setDpiaFilter(e.target.value)}
            className="bg-transparent outline-none"
            data-filter="dpia"
          >
            <option value="all">All DPIAs</option>
            {allGroups.map((g) => (
              <option key={g.key} value={g.key}>
                {g.key === MANUAL_GROUP ? 'Entered in Discovery' : g.reference ?? g.sourceRef ?? 'DPIA'}
              </option>
            ))}
          </select>
        </label>
        <label className="glass-lite flex items-center gap-1.5 rounded-md px-2 py-1 text-xs">
          <span className="text-muted-foreground">Business unit</span>
          <select
            aria-label="Business unit"
            value={unitFilter}
            onChange={(e) => setUnitFilter(e.target.value)}
            className="bg-transparent outline-none"
            data-filter="business-unit"
          >
            <option value="all">All business units</option>
            {hasTenantWide ? <option value={UNIT_FILTER_TENANT_WIDE}>{ALL_UNITS_TEXT} (tenant-wide)</option> : null}
            {hasHeld ? <option value={UNIT_FILTER_HELD}>{HELD_UNIT_TEXT}</option> : null}
            {unitOptions.map((u) => (
              <option key={u.id} value={u.id}>
                {u.name}
              </option>
            ))}
          </select>
        </label>
        <label className="glass-lite flex items-center gap-1.5 rounded-md px-2 py-1 text-xs">
          <span className="text-muted-foreground">Status</span>
          <select
            aria-label="Status"
            value={status}
            onChange={(e) => {
              setStatus(e.target.value as DeclaredStatus);
              setDpiaFilter('all');
              setUnitFilter('all');
            }}
            className="bg-transparent outline-none"
            data-filter="status"
          >
            <option value="active">Active</option>
            <option value="retired">Retired (withdrawn or replaced)</option>
          </select>
        </label>
        {!loading && !loadError ? (
          <span className="text-xs text-muted-foreground" data-testid="declared-count">
            {shown} element{shown === 1 ? '' : 's'}
            {filtered ? ` of ${rows.length}` : ''}
          </span>
        ) : null}
      </div>

      {loadError ? (
        <GlassCard>
          <div className="flex items-start gap-2 p-4 text-sm" role="alert">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-sev-high" />
            <span className="flex-1">{loadError}</span>
            <Button size="sm" variant="glass" onClick={() => void refresh()}>
              Retry
            </Button>
          </div>
        </GlassCard>
      ) : loading && rows.length === 0 ? (
        <div className="flex items-center justify-center gap-2 py-12 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading declared data elements...
        </div>
      ) : rows.length === 0 ? (
        <GlassCard>
          <div className="py-10 text-center text-sm text-muted-foreground" data-testid="declared-empty">
            {status === 'active'
              ? 'No data elements yet. They arrive here when a DPIA is approved in FOCTTA Consent.'
              : 'No retired data elements. Elements retire when a DPIA is withdrawn or a later version leaves them out.'}
          </div>
        </GlassCard>
      ) : groups.length === 0 ? (
        <GlassCard>
          <div className="py-10 text-center text-sm text-muted-foreground">No elements match these filters.</div>
        </GlassCard>
      ) : (
        <div className="space-y-4">
          {groups.map((g) => (
            <GroupCard key={g.key} group={g} unitNames={unitNames} consentUrl={consentUrl} consentAllowed={consentAllowed} />
          ))}
        </div>
      )}
    </PageContainer>
  );
}

function GroupCard({
  group: g,
  unitNames,
  consentUrl,
  consentAllowed,
}: {
  group: ElementGroup;
  unitNames: ReadonlyMap<string, string>;
  consentUrl: string | undefined;
  consentAllowed: boolean;
}): JSX.Element {
  const manual = g.key === MANUAL_GROUP;
  const title = manual ? 'Entered in Discovery' : g.reference ?? g.sourceRef ?? 'DPIA';
  const consentHref = manual ? null : consentDpiaHref(consentUrl, consentAllowed, g.sourceRef);
  const inEffect = g.elements.filter((e) => e.scan.inEffect).length;
  return (
    <GlassCard data-testid={`declared-group-${manual ? 'manual' : g.reference ?? g.sourceRef}`}>
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <CardTitle className="flex items-center gap-2">
              <FileCheck2 className="h-4 w-4 text-foreground/60" />
              {title}
              {!manual && g.version !== null ? (
                <Badge variant="glass" className="text-[10px]">
                  Version {g.version}
                </Badge>
              ) : null}
            </CardTitle>
            <CardDescription>
              {g.elements.length} element{g.elements.length === 1 ? '' : 's'}, {inEffect} in effect in scans
            </CardDescription>
          </div>
          {!manual ? (
            <div className="flex flex-wrap items-center gap-2 text-xs">
              <Link
                to="/dspm/findings"
                search={{ family: 'declared_element_not_found', dpia: g.reference ?? '' } as never}
                className="text-primary hover:underline"
                data-testid="declared-findings-link"
              >
                Findings for this DPIA
              </Link>
              {consentHref ? (
                <a
                  href={consentHref}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="inline-flex items-center gap-1 text-primary hover:underline"
                  data-testid="declared-consent-link"
                >
                  Open in FOCTTA Consent <ExternalLink className="h-3 w-3" />
                </a>
              ) : null}
            </div>
          ) : null}
        </div>
      </CardHeader>
      <div className="overflow-x-auto px-6 pb-6">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
              <th className="pb-3 pr-4 font-medium">Element</th>
              <th className="pb-3 pr-4 font-medium">Category</th>
              <th className="pb-3 pr-4 font-medium">Sensitive</th>
              <th className="pb-3 pr-4 font-medium">Business unit</th>
              <th className="pb-3 pr-4 font-medium">Maps to</th>
              <th className="pb-3 pr-4 font-medium">Scan rule</th>
              <th className="pb-3 font-medium" title="When this element first reached Discovery from FOCTTA Consent">
                Received
              </th>
            </tr>
          </thead>
          <tbody>
            {g.elements.map((e) => {
              const m = mapsTo(e);
              const held = isHeld(e);
              return (
                <tr key={e.id} className="border-b align-top last:border-0" data-testid={`declared-row-${e.id}`}>
                  <td className="py-3 pr-4 font-medium">{e.name}</td>
                  <td className="py-3 pr-4 text-xs">
                    {e.category ?? '-'}
                    {e.piiClass ? <span className="block text-muted-foreground">{e.piiClass}</span> : null}
                  </td>
                  <td className="py-3 pr-4 text-xs">
                    {e.sensitive ? (
                      <Badge variant="high" className="text-[10px]">
                        Sensitive
                      </Badge>
                    ) : (
                      'No'
                    )}
                  </td>
                  <td className={held ? 'py-3 pr-4 text-xs text-warning' : 'py-3 pr-4 text-xs'} data-unit={held ? 'held' : undefined}>
                    {unitText(e, unitNames)}
                  </td>
                  <td className="py-3 pr-4 text-xs">
                    {m.text}
                    {m.detail ? <span className="block max-w-[16rem] break-words text-muted-foreground">{m.detail}</span> : null}
                  </td>
                  <td className="py-3 pr-4">
                    <ScanCell e={e} unitNames={unitNames} />
                  </td>
                  <td className="py-3 text-xs">{ist(e.createdAt)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </GlassCard>
  );
}
